import { serviceRoleClient } from './serviceRoleClient';
import { ENV, SUPABASE_URL } from '../config/env';
import {
  insertBulkRecipients,
  resumeBulkCampaign as resumeBulkCampaignProcessor,
  runBulkCampaignProcessor,
  seedMechanicAudience,
  triggerBulkWorker,
} from './bulkCampaignProcessor';
const fnHeaders = {
  'Content-Type': 'application/json',
  apikey: ENV.SUPABASE_ANON_KEY,
  Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
};

const toError = (err, fallback = 'Request failed') => {
  if (err instanceof Error) return err;
  const msg = err?.message || err?.error_description || err?.details || fallback;
  return new Error(typeof msg === 'string' ? msg : fallback);
};

const isMissingRelation = (error) => {
  const msg = String(error?.message || '');
  return error?.code === '42P01' || error?.code === 'PGRST202' || msg.includes('does not exist') || msg.includes('Could not find');
};

const isStatementTimeout = (error) => {
  const msg = String(error?.message || '');
  return error?.code === '57014' || msg.includes('statement timeout') || msg.includes('canceling statement');
};

/**
 * Insights URL clicks:
 * 1) per-recipient link_clicked_at when present
 * 2) else Meta unique split by sent share across same-template overlapping campaigns
 *    (so two campaigns don't both show the full Meta total)
 */
function urlClickPct(sentCount, clickCount) {
  const sent = Number(sentCount || 0);
  const clicks = Number(clickCount || 0);
  if (!sent || !clicks) return 0;
  return Math.min(100, Math.round((1000 * clicks) / sent) / 10);
}

async function attributedUrlClicks(campaignId, fallback = 0) {
  if (!campaignId) return fallback;
  const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_attributed_url_clicks', {
    p_campaign_id: campaignId,
  });
  if (error) {
    if (!isMissingRelation(error)) console.warn('[url clicks] attributed rpc failed:', error.message);
    return fallback;
  }
  return Number(data || 0);
}

const formatMessagingLimitTier = (tier) => {
  if (tier == null || tier === '') return '—';
  const key = String(tier).trim().toUpperCase();
  const map = {
    TIER_50: '50 users/day',
    TIER_250: '250 users/day',
    TIER_1K: '1K users/day',
    TIER_2K: '2K users/day',
    TIER_10K: '10K users/day',
    TIER_100K: '100K users/day',
    TIER_UNLIMITED: 'Unlimited',
    UNLIMITED: 'Unlimited',
    '2000': '2K users/day',
    '10000': '10K users/day',
    '100000': '100K users/day',
  };
  if (map[key]) return map[key];
  if (/^\d+$/.test(key)) return `${Number(key).toLocaleString('en-IN')} users/day`;
  return String(tier);
};

const pickMessagingLimit = (phoneJson = {}, wabaJson = {}) =>
  phoneJson.whatsapp_business_manager_messaging_limit
  || wabaJson.whatsapp_business_manager_messaging_limit
  || phoneJson.messaging_limit_tier
  || wabaJson.messaging_limit_tier
  || null;

const normalizeAccountDetails = (phoneJson = {}, wabaJson = {}) => {
  const messagingLimit = pickMessagingLimit(phoneJson, wabaJson);
  return {
    verified_name: phoneJson.verified_name || null,
    display_phone_number: phoneJson.display_phone_number || null,
    status: phoneJson.status || 'UNKNOWN',
    name_status: phoneJson.name_status || null,
    quality_rating: phoneJson.quality_rating || 'UNKNOWN',
    messaging_limit_tier: messagingLimit,
    messaging_limit_label: formatMessagingLimitTier(messagingLimit),
    two_fa_enabled: Boolean(
      phoneJson.is_pin_enabled
      || wabaJson.two_step_verification_enabled
      || phoneJson.two_step_verification_enabled,
    ),
    is_official_business_account: Boolean(phoneJson.is_official_business_account),
    fetched_at: new Date().toISOString(),
  };
};

export const EMPTY_STATS = {
  accounts_total: 0,
  accounts_default: 0,
  rules_total: 0,
  rules_enabled: 0,
  ai_enabled: false,
  messages_today: 0,
  messages_total: 0,
  outbound_total: 0,
  inbound_total: 0,
  by_source: {},
  by_status: {},
  by_account: [],
  automation_log_total: 0,
  automation_sent: 0,
  automation_failed: 0,
  bulk_campaigns_total: 0,
  bulk_sent_total: 0,
  customer_segments: {
    all: 0,
    active_membership: 0,
    pending_membership: 0,
    expiring_soon: 0,
    expiring_60_days: 0,
    expiring_90_days: 0,
    expiring_180_days: 0,
    expiring_365_days: 0,
    expired_membership: 0,
    app_installed: 0,
    no_app: 0,
    no_membership: 0,
    has_vehicle: 0,
    no_vehicle: 0,
    whatsapp_contacted: 0,
    ever_responded: 0,
    never_responded: 0,
  },
  daily_outbound: [],
  recent_automation_logs: [],
  recent_bulk_campaigns: [],
  _setup_required: false,
};

export const WhatsAppAutomationService = {
  async listAccounts() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_accounts_safe')
      .select('*')
      .eq('owner_type', 'company')
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load accounts');
    }
    return data || [];
  },

  async saveAccount(payload) {
    const row = {
      name: payload.name,
      phone_number_id: payload.phone_number_id,
      waba_id: payload.waba_id,
      permanent_token_encrypted: payload.permanent_token,
      verify_token: payload.verify_token || null,
      display_phone: payload.display_phone || null,
      owner_type: 'company',
      owner_user_id: null,
      is_default: !!payload.is_default,
      is_active: payload.is_active !== false,
      ai_enabled: !!payload.ai_enabled,
      updated_at: new Date().toISOString(),
    };

    if (payload.id) {
      if (!payload.permanent_token) delete row.permanent_token_encrypted;
      const { data, error } = await serviceRoleClient
        .from('whatsapp_accounts')
        .update(row)
        .eq('id', payload.id)
        .select('id')
        .single();
      if (error) throw toError(error, 'Failed to save account');
      return data;
    }

    const { data, error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .insert(row)
      .select('id')
      .single();
    if (error) throw toError(error, 'Failed to save account');
    return data;
  },

  async deleteAccount(accountId) {
    if (!accountId) throw new Error('Account ID required');
    const { error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .delete()
      .eq('id', accountId);
    if (error) throw toError(error, 'Failed to delete account');
    return { success: true };
  },

  async getAccountDetails(accountId) {
    const { data: acc, error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .select('phone_number_id, waba_id, permanent_token_encrypted')
      .eq('id', accountId)
      .single();
    if (error || !acc) throw toError(error, 'Account not found');

    const phoneFields = [
      'display_phone_number',
      'verified_name',
      'quality_rating',
      'status',
      'name_status',
      'code_verification_status',
      'whatsapp_business_manager_messaging_limit',
      'messaging_limit_tier',
      'is_official_business_account',
      'is_pin_enabled',
    ].join(',');

    const [phoneRes, wabaRes] = await Promise.all([
      fetch(
        `https://graph.facebook.com/v21.0/${acc.phone_number_id}?fields=${phoneFields}`,
        { headers: { Authorization: `Bearer ${acc.permanent_token_encrypted}` } },
      ),
      acc.waba_id
        ? fetch(
          `https://graph.facebook.com/v21.0/${acc.waba_id}?fields=two_step_verification_enabled,whatsapp_business_manager_messaging_limit,messaging_limit_tier`,
          { headers: { Authorization: `Bearer ${acc.permanent_token_encrypted}` } },
        )
        : Promise.resolve(null),
    ]);

    const phoneJson = await phoneRes.json();
    if (!phoneRes.ok) throw new Error(phoneJson?.error?.message || 'Meta phone API failed');

    let wabaJson = {};
    if (wabaRes) {
      wabaJson = await wabaRes.json();
      if (!wabaRes.ok) wabaJson = {};
    }

    return normalizeAccountDetails(phoneJson, wabaJson);
  },

  async testAccount(accountId) {
    const details = await WhatsAppAutomationService.getAccountDetails(accountId);
    return details;
  },

  /** Meta Cloud API registration — moves phone from PENDING → CONNECTED after display name is approved */
  async registerCloudApi(accountId, pin) {
    const pinStr = String(pin || '').replace(/\D/g, '');
    if (!/^\d{6}$/.test(pinStr)) throw new Error('6-digit PIN required (becomes 2FA PIN on Meta)');

    const { data: acc, error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .select('phone_number_id, permanent_token_encrypted')
      .eq('id', accountId)
      .single();
    if (error || !acc) throw toError(error, 'Account not found');

    const res = await fetch(
      `https://graph.facebook.com/v21.0/${acc.phone_number_id}/register`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${acc.permanent_token_encrypted}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messaging_product: 'whatsapp', pin: pinStr }),
      },
    );
    const json = await res.json();
    if (!res.ok) throw new Error(json?.error?.message || 'Cloud API registration failed');
    return WhatsAppAutomationService.getAccountDetails(accountId);
  },

  /**
   * Discover phone numbers on a Meta WABA and insert missing rows into whatsapp_accounts.
   * Uses token/credentials from sourceAccountId (e.g. existing RoadServe account).
   */
  async getAccountCredentials(accountId) {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .select('permanent_token_encrypted, verify_token, waba_id, phone_number_id, name')
      .eq('id', accountId)
      .single();
    if (error || !data) throw toError(error, 'Account not found');
    return data;
  },

  async syncAccountsFromWaba({ sourceAccountId, wabaId, enableAi = false }) {
    if (!sourceAccountId) throw new Error('Source account required');
    if (!wabaId?.trim()) throw new Error('WABA ID required');

    const { data: src, error: srcErr } = await serviceRoleClient
      .from('whatsapp_accounts')
      .select('id, permanent_token_encrypted, verify_token, waba_id')
      .eq('id', sourceAccountId)
      .single();
    if (srcErr || !src?.permanent_token_encrypted) {
      throw toError(srcErr, 'Source account not found or missing token');
    }

    const res = await fetch(
      `https://graph.facebook.com/v21.0/${encodeURIComponent(wabaId.trim())}/phone_numbers?fields=id,display_phone_number,verified_name,status`,
      { headers: { Authorization: `Bearer ${src.permanent_token_encrypted}` } },
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        json?.error?.message
          || 'Meta API failed — assign this WABA to RoadserveApp system user in Meta Business, then retry',
      );
    }

    const phones = Array.isArray(json?.data) ? json.data : [];
    if (!phones.length) {
      return { added: [], phones: [], message: 'No phone numbers on this WABA (still Pending on Meta?)' };
    }

    const existing = await this.listAccounts();
    const existingIds = new Set((existing || []).map((a) => a.phone_number_id));
    const added = [];

    for (const phone of phones) {
      if (!phone?.id || existingIds.has(phone.id)) continue;
      const saved = await this.saveAccount({
        name: phone.verified_name || `WhatsApp ${phone.display_phone_number || ''}`.trim(),
        phone_number_id: phone.id,
        waba_id: wabaId.trim(),
        permanent_token: src.permanent_token_encrypted,
        verify_token: src.verify_token || '',
        display_phone: phone.display_phone_number || null,
        is_default: false,
        is_active: true,
        ai_enabled: !!enableAi,
      });
      added.push({ ...phone, account_id: saved?.id });
      existingIds.add(phone.id);
    }

    return { added, phones, message: added.length ? `Added ${added.length} account(s)` : 'All numbers already in automation' };
  },

  async getCustomerSegmentStats() {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_customer_segment_stats');
    if (error) {
      if (isMissingRelation(error)) return null;
      throw toError(error, 'Failed to load customer segments');
    }
    return data && typeof data === 'object' ? data : null;
  },

  async getStats() {
    const [statsRes, segmentsRes] = await Promise.allSettled([
      serviceRoleClient.rpc('whatsapp_automation_stats', { p_days: 0 }),
      serviceRoleClient.rpc('whatsapp_customer_segment_stats'),
    ]);

    if (statsRes.status === 'rejected') {
      throw toError(statsRes.reason, 'Failed to load stats');
    }

    const { data, error } = statsRes.value;
    if (error) {
      if (isMissingRelation(error)) {
        return { ...EMPTY_STATS, _setup_required: true };
      }
      throw toError(error, 'Failed to load stats');
    }

    const stats = data || {};
    let segments = stats.customer_segments && typeof stats.customer_segments === 'object'
      ? stats.customer_segments
      : {};

    let audienceCache = null;
    if (segmentsRes.status === 'fulfilled' && !segmentsRes.value.error) {
      const segData = segmentsRes.value.data;
      if (segData && typeof segData === 'object') {
        const { cache, ...counts } = segData;
        segments = counts;
        audienceCache = cache || null;
      }
    }

    return {
      ...EMPTY_STATS,
      ...stats,
      outbound_total: stats.outbound_total ?? stats.outbound_period ?? 0,
      inbound_total: stats.inbound_total ?? stats.inbound_period ?? 0,
      bulk_sent_total: stats.bulk_sent_total ?? stats.bulk_sent_period ?? 0,
      bulk_campaigns_total: stats.bulk_campaigns_total ?? stats.bulk_campaigns_period ?? 0,
      automation_log_total: stats.automation_log_total ?? stats.automation_log_period ?? 0,
      by_source: stats.by_source || {},
      by_status: stats.by_status || {},
      by_account: Array.isArray(stats.by_account) ? stats.by_account : [],
      customer_segments: { ...EMPTY_STATS.customer_segments, ...segments },
      audience_cache: audienceCache,
      recent_automation_logs: Array.isArray(stats.recent_automation_logs) ? stats.recent_automation_logs : [],
      recent_bulk_campaigns: Array.isArray(stats.recent_bulk_campaigns) ? stats.recent_bulk_campaigns : [],
      _segments_pending: segmentsRes.status === 'rejected'
        || (segmentsRes.status === 'fulfilled' && !!segmentsRes.value.error && isStatementTimeout(segmentsRes.value.error)),
    };
  },

  async listRules() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load rules');
    }
    return data || [];
  },

  async saveRule(payload) {
    const matchMode = payload.match_mode === 'partial' ? 'partial' : 'exact';
    const row = {
      name: payload.name,
      is_enabled: payload.is_enabled !== false,
      trigger_type: payload.trigger_type,
      trigger_value: String(payload.trigger_value || '').trim(),
      match_mode: payload.trigger_type === 'keyword' ? matchMode : 'exact',
      sender_mode: payload.sender_mode || 'company_default',
      account_id: payload.sender_mode === 'specific_account' ? payload.account_id : null,
      message_mode: payload.message_mode || 'text',
      template_name: payload.template_name || null,
      template_language: payload.template_language || 'en',
      template_params: payload.template_params || [],
      text_body: payload.text_body || null,
      delay_minutes: Number(payload.delay_minutes || 0),
      only_if_unassigned: !!payload.only_if_unassigned,
      enable_ai_reply: !!payload.enable_ai_reply,
      updated_at: new Date().toISOString(),
    };

    if (payload.id) {
      const { data, error } = await serviceRoleClient
        .from('whatsapp_automation_rules')
        .update(row)
        .eq('id', payload.id)
        .select('id')
        .single();
      if (error) throw toError(error, 'Failed to save rule');
      return data;
    }

    const { data, error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .insert(row)
      .select('id')
      .single();
    if (error) throw toError(error, 'Failed to save rule');
    return data;
  },

  async toggleRule(id, enabled) {
    const { error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .update({ is_enabled: enabled, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw toError(error, 'Failed to toggle rule');
  },

  async deleteRule(id) {
    const { error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .delete()
      .eq('id', id);
    if (error) throw toError(error, 'Failed to delete rule');
  },

  async listLogs(limit = 50) {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_automation_log')
      .select('*, whatsapp_automation_rules(name)')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load logs');
    }
    return data || [];
  },

  async syncTemplates(accountId) {
    const url = accountId
      ? `${SUPABASE_URL}/functions/v1/whatsapp-templates?account_id=${accountId}`
      : `${SUPABASE_URL}/functions/v1/whatsapp-templates`;
    const res = await fetch(url, { headers: fnHeaders });
    const json = await res.json().catch(() => ({}));
    if (res.status === 401) {
      throw new Error(json?.message || json?.error || 'Unauthorized — template sync failed');
    }
    return json;
  },

  /** Staff-app order/visibility map keyed by `${name}::${language}` */
  async listTemplateStaffVisibility() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_template_staff_visibility')
      .select('id, template_name, language, show_in_staff_app, sort_order, updated_at')
      .order('sort_order', { ascending: true })
      .order('template_name', { ascending: true });
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load template staff order');
    }
    return data || [];
  },

  /** Show/hide template in TC/TL app (Official WABA list). */
  async upsertTemplateStaffVisibility({
    templateName,
    language = 'en',
    showInStaffApp = true,
    sortOrder = null,
  }) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_upsert_template_staff_visibility', {
      p_template_name: String(templateName || '').trim(),
      p_language: String(language || 'en').trim() || 'en',
      p_show_to_tc: true,
      p_show_to_tl: true,
      p_account_ids: [],
      p_show_in_staff_app: !!showInStaffApp,
      p_sort_order: sortOrder == null ? null : Number(sortOrder),
    });
    if (error) {
      if (isMissingRelation(error)) {
        throw new Error('Run supabase/whatsapp-template-staff-order.sql in Supabase SQL Editor');
      }
      throw toError(error, 'Failed to save template staff settings');
    }
    if (data && data.success === false) {
      throw new Error(data.error || 'Failed to save template staff settings');
    }
    return data?.row || data;
  },

  /**
   * Bulk set staff order for ALL templates.
   * @param {Array<{ template_name: string, language?: string, sort_order: number, show_in_staff_app?: boolean }>} items
   */
  async setTemplateStaffOrders(items) {
    const payload = (items || []).map((it, idx) => ({
      template_name: String(it.template_name || it.name || '').trim(),
      language: String(it.language || 'en').trim() || 'en',
      sort_order: Number.isFinite(Number(it.sort_order)) ? Number(it.sort_order) : (idx + 1) * 10,
      ...(typeof it.show_in_staff_app === 'boolean' ? { show_in_staff_app: it.show_in_staff_app } : {}),
    })).filter((it) => it.template_name);

    const { data, error } = await serviceRoleClient.rpc('whatsapp_set_template_staff_orders', {
      p_items: payload,
    });
    if (error) {
      if (isMissingRelation(error)) {
        throw new Error('Run supabase/whatsapp-template-staff-order-fix.sql in Supabase SQL Editor');
      }
      throw toError(error, 'Failed to save template order');
    }
    if (data && data.success === false) {
      throw new Error(data.error || 'Failed to save template order');
    }
    return data;
  },

  async createTemplate(payload) {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-template-create`, {
      method: 'POST',
      headers: fnHeaders,
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (res.status === 401) {
      throw new Error(json?.message || json?.error || 'Unauthorized — edge function JWT check failed');
    }
    return json;
  },

  async sendBulk(payload) {
    const segmentQueue = !!payload.segment_queue;

    let total = Number(payload.expected_total || 0);
    if (segmentQueue && !total) {
      if (payload.segment === 'wfh') {
        const page = await WhatsAppAutomationService.listWfhLeads({
          teamLeadId: payload.state_filter,
          batchName: payload.search,
          limit: 1,
          offset: 0,
        });
        total = Number(page.total || 0);
      } else if (String(payload.segment || '').startsWith('mechanics')) {
        const page = await WhatsAppAutomationService.listBulkMechanics({
          segment: payload.segment || 'mechanics',
          search: payload.search || '',
          state: payload.state_filter || '',
          limit: 1,
          offset: 0,
        });
        total = Number(page.total || 0);
      } else {
        const page = await WhatsAppAutomationService.listBulkCustomers({
          segment: payload.segment || 'all',
          search: payload.search || '',
          state: payload.state_filter || '',
          limit: 1,
          offset: 0,
        });
        total = Number(page.total || 0);
      }
    } else if (!segmentQueue) {
      total = Array.isArray(payload.recipients) ? payload.recipients.length : 0;
    }

    if (!payload.name?.trim() || total <= 0) {
      return { success: false, error: 'name and recipients required' };
    }

    const templateParamsStored = payload.message_mode === 'template'
      ? {
          body: Array.isArray(payload.template_params) ? payload.template_params : [],
          header_type: payload.template_header_type || 'NONE',
          header_image_url: payload.template_header_image_url || '',
        }
      : (payload.template_params || []);

    const { data: campaign, error: campErr } = await serviceRoleClient
      .from('whatsapp_bulk_campaigns')
      .insert({
        name: payload.name.trim(),
        account_id: payload.account_id || null,
        message_mode: payload.message_mode || 'text',
        template_name: payload.template_name || null,
        template_language: payload.template_language || 'en',
        template_params: templateParamsStored,
        text_body: payload.text_body || null,
        total_recipients: total,
        segment: payload.segment || null,
        search: payload.search || null,
        state_filter: payload.state_filter || null,
        track_links: !!payload.track_links,
        parent_campaign_id: payload.parent_campaign_id || null,
        status: 'pending',
      })
      .select('id')
      .single();

    if (campErr || !campaign) {
      throw toError(campErr, 'Failed to create campaign');
    }

    if (!segmentQueue && payload.recipients?.length) {
      void insertBulkRecipients(campaign.id, payload.recipients, {
        messageMode: payload.message_mode,
        textBody: payload.text_body,
        trackLinks: payload.track_links,
      }).then(() => {
        void runBulkCampaignProcessor(campaign.id);
      }).catch(async (err) => {
        console.error('[sendBulk] recipient insert failed:', err);
        await serviceRoleClient
          .from('whatsapp_bulk_campaigns')
          .update({ status: 'failed', completed_at: new Date().toISOString() })
          .eq('id', campaign.id);
      });
    } else {
      if (String(payload.segment || '').startsWith('mechanics')) {
        await seedMechanicAudience(campaign.id);
        triggerBulkWorker(campaign.id);
      }
      void runBulkCampaignProcessor(campaign.id);
    }

    return {
      success: true,
      async: true,
      campaign_id: campaign.id,
      total,
      status: 'queued',
      message: 'Campaign queued — sending continues in background',
    };
  },

  async resumeBulkCampaign(campaignId) {
    return resumeBulkCampaignProcessor(campaignId);
  },

  async requeueFailedByReason(campaignId, reason, { force = false } = {}) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_requeue_failed_by_reason', {
      p_campaign_id: campaignId,
      p_reason: reason,
      p_force: !!force,
    });
    if (error) throw toError(error, 'Failed to requeue');
    if (data?.success === false) throw new Error(data.error || 'Requeue failed');
    if (Number(data?.requeued || 0) > 0) {
      triggerBulkWorker(campaignId);
      void runBulkCampaignProcessor(campaignId);
    }
    return data;
  },

  async requeueAllRetryableFailures(campaignId) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_requeue_retryable_failures', {
      p_campaign_id: campaignId,
    });
    if (error) throw toError(error, 'Failed to requeue retryable failures');
    if (data?.success === false) throw new Error(data.error || 'Requeue failed');
    if (Number(data?.requeued || 0) > 0) {
      triggerBulkWorker(campaignId);
      void runBulkCampaignProcessor(campaignId);
    }
    return data;
  },

  async cancelBulkCampaign(campaignId) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_cancel_campaign', {
      p_campaign_id: campaignId,
    });
    if (error) throw toError(error, 'Failed to cancel campaign');
    if (data?.success === false) throw new Error(data.error || 'Cancel failed');
    return data;
  },

  /** WFH CSV campaign batches (per team lead, or all). */
  async listWfhBatches(teamLeadId = null) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_wfh_batches', {
      p_team_lead_id: teamLeadId || null,
    });
    if (error) {
      if (isMissingRelation(error)) {
        // Fallback: existing admin RPC requires TL
        if (!teamLeadId) return [];
        const { data: rows, error: e2 } = await serviceRoleClient.rpc('admin_wfh_list_batches', {
          p_team_lead_id: teamLeadId,
        });
        if (e2) throw toError(e2, 'Failed to load WFH batches');
        return (rows || []).map((b) => ({
          team_lead_id: teamLeadId,
          team_lead_name: '',
          batch_name: b.batch_name,
          total_count: Number(b.total_count || 0),
          phone_count: Number(b.total_count || 0),
          is_active: b.is_active !== false,
          last_updated_at: b.last_updated_at || null,
        }));
      }
      throw toError(error, 'Failed to load WFH batches');
    }
    return Array.isArray(data?.batches) ? data.batches : [];
  },

  async listWfhLeads({
    teamLeadId,
    batchName,
    search = '',
    limit = 100,
    offset = 0,
  } = {}) {
    if (!teamLeadId || !batchName) {
      return { items: [], total: 0 };
    }
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_wfh_leads', {
      p_team_lead_id: teamLeadId,
      p_batch_name: batchName,
      p_search: search || '',
      p_limit: limit,
      p_offset: offset,
    });
    if (error) {
      if (isMissingRelation(error)) {
        // Direct table fallback if SQL not deployed yet
        let q = serviceRoleClient
          .from('wfh_leads')
          .select('name, contact_number, car_number, car_model, state, pin_code, batch_name, disposition, status', { count: 'exact' })
          .eq('team_lead_id', teamLeadId)
          .eq('batch_name', batchName)
          .order('created_at', { ascending: false })
          .range(offset, offset + Math.max(limit, 1) - 1);
        if (search?.trim()) {
          const s = search.trim();
          q = q.or(`name.ilike.%${s}%,contact_number.ilike.%${s}%,car_number.ilike.%${s}%,car_model.ilike.%${s}%,state.ilike.%${s}%`);
        }
        const { data: rows, error: e2, count } = await q;
        if (e2) throw toError(e2, 'Failed to load WFH leads');
        const items = (rows || [])
          .map((row) => {
            const digits = String(row.contact_number || '').replace(/\D/g, '');
            const phone = digits.length >= 10 ? digits.slice(-10) : '';
            if (!phone) return null;
            return {
              phone,
              customer_name: row.name || 'Customer',
              car: row.car_number || '',
              car_model: row.car_model || '',
              state: row.state || '',
              pincode: row.pin_code || '',
              batch_name: row.batch_name || '',
              disposition: row.disposition || '',
              lead_status: row.status || '',
              variables: {
                customer_name: row.name || 'Customer',
                phone,
                membership_number: '',
                plan_name: '',
                start_date: '',
                expiry_date: '',
                membership_status: '',
                car: row.car_number || '',
                car_model: row.car_model || '',
                state: row.state || '',
                pincode: row.pin_code || '',
                email: '',
                batch_name: row.batch_name || '',
              },
            };
          })
          .filter(Boolean);
        return { items, total: Number(count ?? items.length), offset, limit };
      }
      throw toError(error, 'Failed to load WFH leads');
    }
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items: items.map((row) => ({
        ...row,
        variables: {
          customer_name: row.customer_name || 'Customer',
          phone: row.phone || '',
          membership_number: '',
          plan_name: '',
          start_date: '',
          expiry_date: '',
          membership_status: '',
          car: row.car || '',
          car_model: row.car_model || '',
          state: row.state || '',
          pincode: row.pincode || '',
          email: '',
          batch_name: row.batch_name || '',
        },
      })),
      total: Number(data?.total ?? items.length),
      offset: Number(data?.offset || offset),
      limit: Number(data?.limit || limit),
    };
  },

  async listBulkMechanics({ segment = 'mechanics', search = '', state = '', limit = 100, offset = 0 } = {}) {
    const mapRow = (row) => {
      const phone = String(row.phone || '').replace(/\D/g, '').slice(-10);
      const name = row.mechanic_name || row.customer_name || row.shop_name || 'Partner';
      const previousCases = String(row.previous_cases ?? row.previous_assigned_cases ?? 0);
      return {
        ...row,
        phone,
        customer_name: name,
        mechanic_name: name,
        previous_cases: previousCases,
        linked: row.linked === true || row.linked === 'true' || !!row.owner_user_id,
        variables: {
          customer_name: name,
          mechanic_name: name,
          phone,
          code: row.code || '',
          shop_name: row.shop_name || '',
          city: row.city || '',
          area: row.area || '',
          state: row.state || '',
          pincode: row.pincode || '',
          partner_type: row.partner_type || '',
          previous_cases: previousCases,
          previous_assigned_cases: previousCases,
          membership_number: '',
          plan_name: '',
          start_date: '',
          expiry_date: '',
          membership_status: '',
          car: '',
          car_model: '',
          email: '',
        },
      };
    };

    const applyMechanicFilters = (q) => {
      if (segment === 'mechanics_active') q = q.eq('active', true);
      if (segment === 'mechanics_inactive') q = q.eq('active', false);
      if (segment === 'mechanics_not_linked') q = q.is('owner_user_id', null);
      if (search?.trim()) {
        const s = search.trim().replace(/[%(),]/g, '');
        q = q.or(`mechanic_name.ilike.%${s}%,shop_name.ilike.%${s}%,code.ilike.%${s}%,number.ilike.%${s}%,city.ilike.%${s}%`);
      }
      if (state?.trim()) {
        const st = state.trim().replace(/[%(),]/g, '');
        q = q.or(`city.ilike.%${st}%,area.ilike.%${st}%`);
      }
      return q;
    };

    const phone10Of = (row) => {
      const digits = String(row.number || row.alternate_number1 || row.phone || '').replace(/\D/g, '');
      return digits.length >= 10 ? digits.slice(-10) : '';
    };

    const countUniquePhones = async () => {
      const unique = new Set();
      for (let from = 0; ; from += 1000) {
        let q = applyMechanicFilters(
          serviceRoleClient.from('mechanics').select('number, alternate_number1'),
        );
        const { data, error: countErr } = await q.range(from, from + 999);
        if (countErr) throw toError(countErr, 'Failed to count mechanics');
        (data || []).forEach((row) => {
          const phone = phone10Of(row);
          if (phone) unique.add(phone);
        });
        if (!data || data.length < 1000) break;
      }
      return unique.size;
    };

    const loadFromTable = async () => {
      const [uniqueTotal, pageRes] = await Promise.all([
        countUniquePhones(),
        applyMechanicFilters(
          serviceRoleClient
            .from('mechanics')
            .select('id, mechanic_name, shop_name, number, alternate_number1, code, city, area, partner_type, active, owner_user_id, previous_assigned_cases, service_areas')
            .order('mechanic_name', { ascending: true, nullsFirst: false }),
        ).range(offset, offset + Math.max(limit * 3, 1) - 1),
      ]);
      const { data: rows, error: e2 } = pageRes;
      if (e2) throw toError(e2, 'Failed to load mechanics');
      const seen = new Set();
      const items = (rows || [])
        .map((row) => {
          const phone = phone10Of(row);
          if (!phone || seen.has(phone)) return null;
          seen.add(phone);
          const areas = Array.isArray(row.service_areas) ? row.service_areas : [];
          return mapRow({
            phone,
            mechanic_name: row.mechanic_name || row.shop_name || 'Partner',
            code: row.code || '',
            shop_name: row.shop_name || '',
            city: row.city || '',
            area: row.area || '',
            state: areas.find((a) => a?.state)?.state || '',
            pincode: areas.find((a) => a?.pincode)?.pincode || '',
            partner_type: row.partner_type || '',
            previous_cases: Number(row.previous_assigned_cases || 0),
            active: !!row.active,
            owner_user_id: row.owner_user_id || null,
            linked: !!row.owner_user_id,
          });
        })
        .filter(Boolean)
        .slice(0, limit);
      return { items, total: uniqueTotal, offset, limit, _fallback: true };
    };

    // Old SQL ignores unknown segments — query table so "not linked" never sends everyone.
    if (segment === 'mechanics_not_linked') {
      return loadFromTable();
    }

    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_mechanics', {
      p_segment: segment,
      p_search: search,
      p_state: state,
      p_limit: limit,
      p_offset: offset,
    });
    if (error) {
      if (isMissingRelation(error)) {
        return loadFromTable();
      }
      throw toError(error, 'Failed to load mechanics');
    }
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items: items.map(mapRow).filter((row) => row.phone),
      total: Number(data?.total ?? items.length),
      offset: Number(data?.offset || offset),
      limit: Number(data?.limit || limit),
    };
  },

  async listBulkCustomers({ segment = 'all', search = '', state = '', limit = 100, offset = 0 } = {}) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_customers', {
      p_segment: segment,
      p_search: search,
      p_limit: limit,
      p_offset: offset,
      p_state: state,
    });
    if (error) {
      if (isMissingRelation(error)) {
        return { items: [], total: 0, _setup_required: true };
      }
      throw toError(error, 'Failed to load customers');
    }
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items: items.map((row) => ({
        ...row,
        app_installed: !!row.app_installed,
        variables: {
          customer_name: row.customer_name || 'Customer',
          phone: row.phone || '',
          membership_number: row.membership_number || '',
          plan_name: row.plan_name || '',
          start_date: row.start_date || '',
          expiry_date: row.expiry_date || '',
          membership_status: row.membership_status || '',
          app_installed: row.app_installed ? 'Yes' : 'No',
          car: row.car || '',
          car_model: row.car_model || '',
          state: row.state || '',
          pincode: row.pincode || '',
          email: row.email || '',
        },
      })),
      total: Number(data?.total ?? items.length),
      offset: Number(data?.offset || offset),
      limit: Number(data?.limit || limit),
      cache: data?.cache || null,
    };
  },

  /** Preview only — never pull 1L rows into the browser. Send uses segment_queue. */
  async listAllBulkCustomers(filters = {}) {
    return WhatsAppAutomationService.listBulkCustomers({
      ...filters,
      limit: 100,
      offset: 0,
    });
  },

  async getBulkAudienceStatus() {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_audience_status');
    if (error) {
      if (isMissingRelation(error)) return { empty: true, _setup_required: true };
      throw toError(error, 'Failed to load audience cache status');
    }
    return data || { empty: true };
  },

  async refreshBulkAudience() {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_refresh_audience');
    if (error) {
      if (isMissingRelation(error)) {
        return {
          success: false,
          _setup_required: true,
          error: 'Run whatsapp-bulk-audience-cache.sql in Supabase first',
        };
      }
      throw toError(error, 'Audience cache refresh failed (run SQL in Supabase if gateway times out)');
    }
    return data || { success: true };
  },

  async listBulkCampaigns(limit = 50) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_campaigns', { p_limit: limit });
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load campaigns');
    }
    return Array.isArray(data) ? data : [];
  },

  /** Pull Meta URL button + short-link clicks onto campaigns. */
  async syncBulkCampaignMetaUrlClicks(campaignIds = []) {
    const ids = (Array.isArray(campaignIds) ? campaignIds : []).filter(Boolean).slice(0, 20);
    if (!ids.length) return { success: true, results: [] };
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-campaign-meta-clicks`, {
      method: 'POST',
      headers: fnHeaders,
      body: JSON.stringify({ campaign_ids: ids }),
    });
    const json = await res.json().catch(() => ({}));
    if (!json?.success && json?.error) {
      throw toError(json, 'Failed to sync Meta URL clicks');
    }
    return json;
  },

  /** Insights URL clicks = tracked recipients, else Meta split across same-template campaigns. */
  async enrichBulkCampaignUrlClicks(campaigns = []) {
    const rows = Array.isArray(campaigns) ? campaigns : [];
    const enriched = [];
    for (const campaign of rows) {
      const sent = Number(campaign.sent_count || 0);
      if (sent <= 0) {
        enriched.push({
          ...campaign,
          link_click_count: 0,
          link_click_pct: 0,
        });
        if (Number(campaign.link_click_count || 0) > 0) {
          serviceRoleClient
            .from('whatsapp_bulk_campaigns')
            .update({ link_click_count: 0 })
            .eq('id', campaign.id)
            .then(({ error }) => {
              if (error) console.warn('[url clicks] clear failed:', error.message);
            });
        }
        continue;
      }

      const clicks = Math.min(
        await attributedUrlClicks(campaign.id, Number(campaign.link_click_count || 0)),
        sent,
      );
      const next = {
        ...campaign,
        link_click_count: clicks,
        link_click_pct: urlClickPct(sent, clicks),
        meta_url_click_pct: urlClickPct(
          sent,
          Math.min(
            Math.max(
              Number(campaign.meta_url_click_unique || 0),
              Number(campaign.meta_url_click_count || 0),
            ),
            sent,
          ),
        ),
      };
      enriched.push(next);

      if (clicks !== Number(campaign.link_click_count || 0)) {
        serviceRoleClient
          .from('whatsapp_bulk_campaigns')
          .update({ link_click_count: clicks })
          .eq('id', campaign.id)
          .then(({ error }) => {
            if (error) console.warn('[url clicks] campaign update failed:', error.message);
          });
      }
    }
    return enriched;
  },

  async getBulkCampaignDetail(campaignId) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_campaign_detail', {
      p_campaign_id: campaignId,
    });
    if (error) throw toError(error, 'Failed to load campaign detail');
    return data || {};
  },

  async getRetargetRecipients(campaignId, filter = 'no_response') {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_retarget_recipients', {
      p_campaign_id: campaignId,
      p_filter: filter,
    });
    if (error) throw toError(error, 'Failed to load retarget list');
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      filter: data?.filter || filter,
      total: Number(data?.total || items.length),
      items: items.map((row) => ({
        ...row,
        phone: row.phone,
        customer_name: row.customer_name || row.variables?.customer_name || 'Customer',
        variables: row.variables || {
          customer_name: row.customer_name || 'Customer',
          phone: row.phone || '',
          car: row.car || '',
          car_model: row.car_model || '',
          state: row.state || '',
          pincode: row.pincode || '',
          membership_status: row.membership_status || '',
          expiry_date: row.expiry_date || '',
        },
      })),
    };
  },

  async getGlobalAiMemory() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_ai_memory')
      .select('*')
      .eq('id', 1)
      .maybeSingle();
    if (error) {
      if (isMissingRelation(error)) {
        return { system_prompt: '', global_enabled: false, escalation_keywords: [] };
      }
      throw toError(error, 'Failed to load global AI memory');
    }
    return data;
  },

  /** @deprecated use getAiMemoryForAccount — kept for callers that still expect global row */
  async getAiMemory() {
    return this.getGlobalAiMemory();
  },

  async getAiMemoryForAccount(accountId) {
    if (!accountId) {
      const global = await this.getGlobalAiMemory();
      return {
        account_id: null,
        system_prompt: global?.system_prompt || '',
        model: global?.model || 'gpt-4o-mini',
        max_tokens: global?.max_tokens ?? 300,
        temperature: global?.temperature ?? 0.7,
        enabled: !!global?.global_enabled,
        global_enabled: !!global?.global_enabled,
        business_hours: global?.business_hours || {},
        escalation_keywords: global?.escalation_keywords || [],
        fallback_template_name: global?.fallback_template_name || 'service_booking_update',
        fallback_template_language: global?.fallback_template_language || 'en',
        _source: 'global',
      };
    }

    const [{ data: global }, { data: account }, acctMemResult] = await Promise.all([
      serviceRoleClient.from('whatsapp_ai_memory').select('*').eq('id', 1).maybeSingle(),
      serviceRoleClient
        .from('whatsapp_accounts_safe')
        .select('id, name, display_phone, waba_id, phone_number_id, ai_enabled, is_default')
        .eq('id', accountId)
        .maybeSingle(),
      serviceRoleClient
        .from('whatsapp_ai_memory_by_account')
        .select('*')
        .eq('account_id', accountId)
        .maybeSingle(),
    ]);

    if (acctMemResult.error && !isMissingRelation(acctMemResult.error)) {
      throw toError(acctMemResult.error, 'Failed to load account AI memory');
    }

    const row = acctMemResult.data;
    const fallbackPrompt = global?.system_prompt || '';

    return {
      account_id: accountId,
      account: account || null,
      system_prompt: row?.system_prompt || fallbackPrompt,
      model: row?.model || global?.model || 'gpt-4o-mini',
      max_tokens: row?.max_tokens ?? global?.max_tokens ?? 300,
      temperature: row?.temperature ?? global?.temperature ?? 0.7,
      enabled: row ? !!row.enabled : !!account?.ai_enabled,
      global_enabled: !!global?.global_enabled,
      business_hours: row?.business_hours || global?.business_hours || {},
      escalation_keywords: row?.escalation_keywords || global?.escalation_keywords || [],
      fallback_template_name: row?.fallback_template_name || global?.fallback_template_name || 'service_booking_update',
      fallback_template_language: row?.fallback_template_language || global?.fallback_template_language || 'en',
      _source: row ? 'account' : 'fallback_global',
      _has_account_row: !!row,
    };
  },

  async saveAiMemory(payload) {
    const accountId = payload.account_id || null;
    const now = new Date().toISOString();

    // Master kill-switch (global row) — update only the flag when saving per-account memory
    if (payload.global_enabled != null) {
      const global = await this.getGlobalAiMemory();
      const { error: ge } = await serviceRoleClient
        .from('whatsapp_ai_memory')
        .upsert({
          id: 1,
          system_prompt: accountId ? (global?.system_prompt || '') : (payload.system_prompt || ''),
          model: payload.model || global?.model || 'gpt-4o-mini',
          max_tokens: Number(payload.max_tokens || global?.max_tokens || 300),
          temperature: Number(payload.temperature ?? global?.temperature ?? 0.7),
          global_enabled: !!payload.global_enabled,
          business_hours: accountId ? (global?.business_hours || {}) : (payload.business_hours || {}),
          escalation_keywords: accountId
            ? (global?.escalation_keywords || [])
            : (payload.escalation_keywords || []),
          fallback_template_name: accountId
            ? (global?.fallback_template_name || 'service_booking_update')
            : (payload.fallback_template_name || 'service_booking_update'),
          fallback_template_language: accountId
            ? (global?.fallback_template_language || 'en')
            : (payload.fallback_template_language || 'en'),
          updated_at: now,
        });
      if (ge && !accountId) throw toError(ge, 'Failed to save AI memory');
    }

    if (!accountId) {
      const { data, error } = await serviceRoleClient
        .from('whatsapp_ai_memory')
        .upsert({
          id: 1,
          system_prompt: payload.system_prompt || '',
          model: payload.model || 'gpt-4o-mini',
          max_tokens: Number(payload.max_tokens || 300),
          temperature: Number(payload.temperature || 0.7),
          global_enabled: !!payload.global_enabled,
          business_hours: payload.business_hours || {},
          escalation_keywords: payload.escalation_keywords || [],
          fallback_template_name: payload.fallback_template_name || 'service_booking_update',
          fallback_template_language: payload.fallback_template_language || 'en',
          updated_at: now,
        })
        .select('*')
        .single();
      if (error) throw toError(error, 'Failed to save AI memory');
      return data;
    }

    const enabled = payload.enabled != null ? !!payload.enabled : false;
    const { data, error } = await serviceRoleClient
      .from('whatsapp_ai_memory_by_account')
      .upsert({
        account_id: accountId,
        system_prompt: payload.system_prompt || '',
        model: payload.model || 'gpt-4o-mini',
        max_tokens: Number(payload.max_tokens || 300),
        temperature: Number(payload.temperature || 0.7),
        enabled,
        business_hours: payload.business_hours || {},
        escalation_keywords: payload.escalation_keywords || [],
        fallback_template_name: payload.fallback_template_name || 'service_booking_update',
        fallback_template_language: payload.fallback_template_language || 'en',
        updated_at: now,
      }, { onConflict: 'account_id' })
      .select('*')
      .single();

    if (error) {
      if (isMissingRelation(error)) {
        throw toError(
          error,
          'Per-account AI memory table missing — run supabase/whatsapp-ai-memory-per-account.sql',
        );
      }
      throw toError(error, 'Failed to save account AI memory');
    }

    if (payload.sync_account_ai !== false) {
      await serviceRoleClient
        .from('whatsapp_accounts')
        .update({ ai_enabled: enabled, updated_at: now })
        .eq('id', accountId);
    }

    return this.getAiMemoryForAccount(accountId);
  },

  async testAiReply(message, phone, accountId = null) {
    // Preview via Edge Function only — same OPENAI_API_KEY secret as live WhatsApp chat.
    // Do NOT pass web-app ENV.OPENAI_API_KEY (often stale and overrides the working Edge secret).
    const contactPhone = phone || '919999999999';
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-ai-reply`, {
      method: 'POST',
      headers: fnHeaders,
      body: JSON.stringify({
        contact_phone: contactPhone,
        message_content: message || 'hi',
        dry_run: true,
        account_id: accountId || null,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: json?.error || `AI preview failed (${res.status})`,
        phone: contactPhone,
      };
    }
    if (!json?.success) {
      return {
        success: false,
        error: json?.error || json?.reason || 'AI preview failed',
        phone: contactPhone,
      };
    }
    return {
      success: true,
      reply: json.reply || '',
      dry_run: true,
      phone: contactPhone,
      history_count: json.history_count,
    };
  },

  getWebhookUrl() {
    return `${SUPABASE_URL}/functions/v1/whatsapp-webhook`;
  },

  /** Read legacy WHATSAPP_* edge secrets via edge function (verify_jwt must be off). */
  async getSupabaseEnvCredentials() {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-env-import`, {
      method: 'POST',
      headers: {
        ...fnHeaders,
        Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ action: 'get' }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json?.success) {
      throw new Error(json?.error || `Import failed (${res.status})`);
    }
    return json.credentials;
  },

  /**
   * Generate Meta-compliant WhatsApp template draft via Supabase Edge
   * (same OPENAI_API_KEY secret as live WhatsApp AI — not marketing-site's stale key).
   */
  async generateTemplateWithAi({ category, language, brief, templateName = '' }) {
    if (!brief?.trim()) throw new Error('Describe what this template is about');

    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-template-ai-generate`, {
      method: 'POST',
      headers: fnHeaders,
      body: JSON.stringify({
        category: category || 'UTILITY',
        language: language || 'en',
        brief: brief.trim(),
        template_name: templateName || '',
      }),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.error || `AI generation failed (${res.status})`);
    }
    if (!json?.success || !json?.draft) {
      throw new Error(json?.error || 'AI generation failed');
    }
    return json.draft;
  },

  async listAiComplaintDrafts({ status = 'pending_review', limit = 50 } = {}) {
    let q = serviceRoleClient
      .from('whatsapp_ai_complaint_drafts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (status && status !== 'all') q = q.eq('status', status);
    const { data, error } = await q;
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load AI drafts');
    }
    return data || [];
  },

  async getPendingDraftPhones() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_ai_complaint_drafts')
      .select('contact_phone')
      .eq('status', 'pending_review');
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load draft phones');
    }
    return new Set((data || []).map((r) => r.contact_phone));
  },

  async updateAiComplaintDraft(id, patch) {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_ai_complaint_drafts')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw toError(error, 'Failed to update draft');
    return data;
  },

  async rejectAiComplaintDraft(id, reviewedBy = null) {
    return this.updateAiComplaintDraft(id, {
      status: 'rejected',
      reviewed_by: reviewedBy,
      reviewed_at: new Date().toISOString(),
    });
  },

  async confirmAiComplaintDraft(id, { reviewedBy = null, reviewedName = 'WhatsApp Admin' } = {}) {
    const { data: draft, error: loadErr } = await serviceRoleClient
      .from('whatsapp_ai_complaint_drafts')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (loadErr) throw toError(loadErr, 'Failed to load draft');
    if (!draft) throw new Error('Draft not found');
    if (draft.status === 'converted') return draft;

    const phone10 = String(draft.contact_phone || '').replace(/\D/g, '').slice(-10);
    if (phone10.length !== 10) throw new Error('Invalid phone on draft');

    const problemText = [
      draft.problem,
      draft.address ? `Pickup: ${draft.address}` : '',
      draft.drop_location ? `Drop: ${draft.drop_location}` : '',
      draft.fuel_delivery_requested ? 'Fuel delivery RSA (operational)' : '',
    ].filter(Boolean).join('\n');

    const remarkParts = [
      draft.summary || 'Confirmed from WhatsApp AI draft',
      draft.registration_fee_acknowledged ? 'Customer acknowledged ₹199 registration fee' : '',
      draft.fuel_delivery_requested ? 'Fuel delivery — operational WhatsApp RSA' : '',
    ].filter(Boolean);

    const fuelServiceType = (() => {
      if (!draft.fuel_delivery_requested) return draft.service_type || 'other';
      const blob = `${draft.problem || ''} ${draft.summary || ''}`.toLowerCase();
      if (blob.includes('diesel')) return 'Fuel Delivery · Diesel';
      if (blob.includes('petrol')) return 'Fuel Delivery · Petrol';
      return 'Fuel Delivery';
    })();

    const { data: rpcData, error: rpcErr } = await serviceRoleClient.rpc('service_executive_create_request', {
      p_request_data: {
        customer_name: draft.contact_name || 'WhatsApp Customer',
        vehicle_number: draft.vehicle_number || '',
        service_type: fuelServiceType || draft.service_type || 'other',
        priority: draft.intent === 'emergency' ? 'high' : 'medium',
        contact_number: phone10,
        alternate_number: '',
        problem: problemText,
        address: draft.address || '',
        pincode: '',
        customer_quoted_amount: Number(draft.customer_quoted_amount) || (draft.fuel_delivery_requested ? 0 : 199),
        advance_payment: draft.advance_payment || '',
        service_tag: draft.fuel_delivery_requested ? 'fuel_delivery' : '',
        location_link: '',
        media_upload: [],
        source: 'whatsapp_ai',
        remark: remarkParts.join(' | '),
        is_premium: false,
        card_number: '',
        registered_by_executive_id: reviewedBy || null,
        registered_by_executive_name: reviewedName,
        request_status: 'pending',
        complaint_status: draft.fuel_delivery_requested ? 'registered' : 'registered',
        drop_location: draft.drop_location || '',
      },
    });

    if (rpcErr) throw toError(rpcErr, 'Failed to create complaint');
    const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
    if (!row?.success) throw new Error(row?.message || 'Complaint creation failed');

    const { data: updated, error: updErr } = await serviceRoleClient
      .from('whatsapp_ai_complaint_drafts')
      .update({
        status: 'converted',
        service_request_id: row.id,
        reviewed_by: reviewedBy,
        reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*')
      .single();
    if (updErr) throw toError(updErr, 'Complaint created but draft update failed');
    return { draft: updated, requestId: row.id };
  },
};
